import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, SelectHierarchyLabel, ToastContext } from '@weezevent/nacre';

import { Domain, Organization } from 'models';

import { LoadingOptions } from 'components';

import css from '../domains.module.css';

export const AddOrganizationModal = ({ open, onClose, domain, syncDomain }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [search, setSearch] = React.useState('');

    const [organizationId, setOrganizationId] = React.useState(null);

    const [organizations, loadingOrganizations] = Organization.useApiModel(
        {
            cache: false,
            allow_cache: false,
            query: { search, limit: 50, offset: 0 },
            launch: open
        },
        [search, open]
    );

    const organizationsOptions = React.useMemo(() => {
        if (loadingOrganizations) return LoadingOptions;
        if (!organizations) return [];

        return organizations.map(org => org.toOption());
    }, [organizations, loadingOrganizations]);

    const handleSubmit = React.useCallback(
        ({ close }) => {
            const payload = new Domain({ name: domain.name, organizerId: organizationId });
            return payload
                .save({ organizationId })
                .then(response => {
                    if (response.status === 201) {
                        toast.success(t('sauron.toasts.domains.add-organization-success'));
                        syncDomain();
                    }
                })
                .catch(() => {
                    toast.error(t('sauron.toasts.error'));
                })
                .finally(() => {
                    if (close) onClose();
                });
        },
        [domain.name, organizationId, syncDomain, onClose]
    );
    return (
        <Modal
            className={css['modal-container']}
            size={'large'}
            open={open}
            allowExternalClosing={true}
            onClose={onClose}
            title={t('sauron.emails.domains.add-organization')}
            subtitle={t('sauron.emails.domains.add-organization-subtitle')}
            customFooter={
                <Button.Group>
                    <Button onClick={onClose} label={t('common.cta.cancel')} />
                    <Button primary inverted onClick={() => handleSubmit({ close: true })} label={t('common.cta.save')} />
                    <Button primary onClick={handleSubmit} label={t('sauron.emails.domains.save-and-add')} />
                </Button.Group>
            }
        >
            <SelectHierarchyLabel
                searchable
                value={organizationId}
                options={organizationsOptions}
                externalOptions={organizationsOptions}
                onChange={value => setOrganizationId(value)}
                label={t('common.organization')}
                optionalText={t('common.optional')}
                placeholder={t('common.select')}
                placeholderSearch={t('common.search')}
                searchValue={search}
                onExternalSearch={value => setSearch(value)}
            />
        </Modal>
    );
};
